import { t } from "@/i18n";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Check, Download, FileText, GripVertical, Loader2, X } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { api, formatApiError, pageImageUrl, downloadFile, slug } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { ThemeGenerator } from "@/pages/ThemeGenerator";
import { TemplateGallery } from "@/pages/TemplateGallery";

const SelectedRow = ({ page, index, onRemove }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={page.id} dragListener={false} dragControls={controls}
      whileDrag={{ scale: 1.03, boxShadow: "0 12px 24px -8px rgba(30,41,59,0.35)" }}
      className="flex items-center gap-3 bg-white rounded-2xl border-2 border-slate-200 p-2 pr-3 select-none" data-testid={`packet-order-item-${page.id}`}>
      <span onPointerDown={(e) => controls.start(e)} style={{ touchAction: "none" }} title={t("drag_to_reorder")}
        className="p-1 rounded-lg cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#1E293B] hover:bg-slate-100" data-testid={`packet-drag-handle-${page.id}`}>
        <GripVertical className="w-5 h-5" />
      </span>
      <span className="w-7 h-7 rounded-lg bg-[#1E293B] text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
      <img src={pageImageUrl(page.id)} alt="" draggable={false} className="w-12 h-12 object-contain rounded-lg border bg-white" />
      <span className="flex-1 font-bold text-sm truncate">{page.title}</span>
      <button onClick={() => onRemove(page.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`packet-remove-${page.id}`}><X className="w-4 h-4" /></button>
    </Reorder.Item>
  );
};

export default function Packets() {
  const loc = useLocation();
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(loc.state?.preselect ? [loc.state.preselect] : []);
  const [title, setTitle] = useState("My Colouring Packet");
  const [className, setClassName] = useState("");
  const [date, setDate] = useState(() => new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }));
  const [facts, setFacts] = useState(true);
  const [quiz, setQuiz] = useState(true);
  const [stories, setStories] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [packets, setPackets] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/pages").then((r) => setPages(r.data));
    api.get("/packets").then((r) => setPackets(r.data));
    api.get("/catalog").then((r) => setTemplates(r.data.templates || []));
  }, []);

  const byId = Object.fromEntries(pages.map((p) => [p.id, p]));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onTemplateDone = (newPages, packet) => {
    setPages((p) => [...newPages, ...p]);
    setPackets((k) => [packet, ...k]);
  };

  const onThemePages = (newPages, themeTitle) => {
    setPages((p) => [...newPages, ...p]);
    setSelected((s) => [...newPages.map((p) => p.id), ...s]);
    setTitle(themeTitle);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { data } = await api.post("/packets", { title, page_ids: selected, include_facts: facts, include_quiz: quiz, include_stories: stories, class_name: className, date }, { timeout: 300000 });
      setPackets((p) => [data, ...p]);
      await downloadFile(`/packets/${data.id}/pdf`, `${slug(data.title)}.pdf`);
      toast.success("PDF packet ready!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const ordered = selected.map((id) => byId[id]).filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 pb-36" data-testid="packets-page">
      <div className="mb-8 rise">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("packet_builder")}</h1>
        <p className="text-slate-600 mt-2 text-base sm:text-lg">{t("generate_a_themed_set_pick_and_order_pag")}</p>
      </div>

      {templates.length > 0 && <div className="mb-10"><TemplateGallery templates={templates} onDone={onTemplateDone} cover={{ class_name: className, date }} /></div>}

      <ThemeGenerator credits={user.credits} onPages={onThemePages} />

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        <section className="lg:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-semibold">{t("your_pages")}</h2>
            <div className="flex gap-2">
              <button className="text-sm font-bold text-slate-600 hover:text-[#1E293B] px-3 py-1.5 rounded-lg hover:bg-slate-100" onClick={() => setSelected(pages.map((p) => p.id))} data-testid="packet-select-all">{t("select_all")}</button>
              <button className="text-sm font-bold text-slate-600 hover:text-[#1E293B] px-3 py-1.5 rounded-lg hover:bg-slate-100" onClick={() => setSelected([])} data-testid="packet-clear-selection">{t("clear")}</button>
            </div>
          </div>
          {pages.length === 0 ? <p className="card-soft p-10 text-center text-slate-500">{t("no_pages_yet_generate_a_themed_set_above")}</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {pages.map((p) => {
                const on = selected.includes(p.id);
                return (
                  <button key={p.id} onClick={() => toggle(p.id)} data-testid={`packet-page-card-${p.id}`}
                    className={`relative text-left bg-white rounded-3xl p-2 border-4 transition-colors ${on ? "border-[#FF5252]" : "border-slate-100 hover:border-slate-300"}`}>
                    <span data-testid={`packet-page-checkbox-${p.id}`} className={`absolute top-3 right-3 w-8 h-8 rounded-full border-2 flex items-center justify-center ${on ? "bg-[#FF5252] border-[#FF5252] text-white" : "bg-white border-slate-300"}`}>
                      {on && <Check className="w-5 h-5" />}
                    </span>
                    <img src={pageImageUrl(p.id)} alt={p.title} loading="lazy" className="w-full aspect-square object-contain rounded-2xl" />
                    <p className="text-sm font-bold px-2 py-2 truncate">{p.title}</p>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <aside className="lg:col-span-4 space-y-6">
          <div className="card-soft p-6 space-y-5">
            <h2 className="font-display text-2xl font-semibold">{t("packet_settings")}</h2>
            <label className="block">
              <span className="text-sm font-bold text-slate-700">{t("packet_title")}</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} data-testid="packet-title-input"
                className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none focus:border-[#FF5252]" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">{t("class_name")}</span>
                <input value={className} onChange={(e) => setClassName(e.target.value)} maxLength={60} placeholder={t("e_g_year_2b")} data-testid="packet-class-input"
                  className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none focus:border-[#FF5252]" />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">{t("date")}</span>
                <input value={date} onChange={(e) => setDate(e.target.value)} maxLength={40} placeholder={t("e_g_term_2_week_3")} data-testid="packet-date-input"
                  className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none focus:border-[#FF5252]" />
              </label>
            </div>
            <p className="text-xs text-slate-500 -mt-2">{t("printed_on_the_packet_cover_also_used_by")}</p>
            <div className="flex items-center justify-between"><span className="font-bold">{t("include_fun_facts")}</span><Switch checked={facts} onCheckedChange={setFacts} data-testid="fun-fact-toggle" /></div>
            <div className="flex items-center justify-between"><span className="font-bold">{t("include_mini_quizzes")}</span><Switch checked={quiz} onCheckedChange={setQuiz} data-testid="quiz-toggle" /></div>
            <div className="flex items-center justify-between gap-3">
              <div><span className="font-bold flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[#FF5252]" /> {t("include_read_aloud_stories")}</span>
                <p className="text-xs text-slate-500">{t("ai_writes_a_gentle_story_page_after_each")}</p></div>
              <Switch checked={stories} onCheckedChange={setStories} data-testid="stories-toggle" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">{t("page_order")}</p>
              {ordered.length === 0 ? <p className="text-sm text-slate-500">{t("tap_pages_to_add_them")}</p> : (
                <Reorder.Group as="ol" axis="y" layoutScroll values={ordered.map((p) => p.id)} onReorder={setSelected}
                  className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="packet-order-list">
                  {ordered.map((p, i) => <SelectedRow key={p.id} page={p} index={i} onRemove={toggle} />)}
                </Reorder.Group>
              )}
            </div>
          </div>
          {packets.length > 0 && (
            <div className="card-soft p-6" data-testid="previous-packets">
              <h3 className="font-display text-xl font-semibold mb-3">{t("previous_packets")}</h3>
              <ul className="space-y-2">
                {packets.map((k) => (
                  <li key={k.id} className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#FF5252] shrink-0" />
                    <span className="flex-1 text-sm font-bold truncate">{k.title} <span className="text-slate-400 font-normal">· {k.page_ids.length} pages</span></span>
                    <button className="p-2 rounded-lg hover:bg-slate-100" onClick={() => downloadFile(`/packets/${k.id}/pdf`, `${slug(k.title)}.pdf`)} data-testid={`packet-download-${k.id}`}><Download className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <div className="fixed bottom-6 left-0 right-0 px-4 z-40 pointer-events-none">
        <div className="pointer-events-auto mx-auto max-w-4xl bg-slate-900 text-white rounded-2xl p-4 shadow-2xl flex items-center justify-between gap-4 border border-slate-700" data-testid="packet-action-bar">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-[#FFD166] text-[#1E293B] font-bold flex items-center justify-center" data-testid="packet-selected-count">{ordered.length}</span>
            <span className="font-bold hidden sm:block">pages selected {facts || quiz || stories ? `· ${[facts && "facts", quiz && "quizzes", stories && "stories"].filter(Boolean).join(" + ")}` : ""}</span>
          </div>
          <button className="btn-pop btn-red" disabled={!ordered.length || exporting || !title.trim()} onClick={exportPdf} data-testid="export-pdf-button">
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} {exporting && stories ? "Writing stories & building PDF..." : "Export PDF packet"}
          </button>
        </div>
      </div>
    </div>
  );
}
